import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";
import { Bike, MapPin, Clock, Utensils, CheckCircle2, ShieldCheck } from "lucide-react";

const EMPTY = { nome: "", cognome: "", telefono: "", partecipanti: "1", moto: "1", pranzo: null, persone_pranzo: "", note: "" };

export default function Motoraduno() {
  const { settings } = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tried, setTried] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { track("page_view", "motoraduno"); }, []);

  const partecipanti = Number(form.partecipanti) || 0;
  const moto = Number(form.moto) || 0;
  const personePranzo = Number(form.persone_pranzo) || 0;
  const errors = {
    pranzo: form.pranzo === null ? "Indica se ti fermi a pranzo" : "",
    persone_pranzo: form.pranzo === true && personePranzo < 1 ? "Indica quante persone si fermano a pranzo"
      : form.pranzo === true && personePranzo > partecipanti ? "Le persone a pranzo non possono superare i partecipanti" : "",
    moto: moto > partecipanti ? "Le moto non possono superare i partecipanti" : "",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const scrollToForm = () => {
    document.getElementById("partecipa")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setTried(true);
    if (hasErrors) return;
    setLoading(true);
    try {
      await api.post("/moto-signups", {
        nome: form.nome, cognome: form.cognome, telefono: form.telefono,
        partecipanti, moto, pranzo: form.pranzo === true,
        persone_pranzo: form.pranzo === true ? personePranzo : 0, note: form.note,
      });
      setDone(true);
      setForm(EMPTY); setTried(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Controlla i campi e riprova.");
    } finally { setLoading(false); }
  };

  const mapEmbed = settings.moto_map_embed || "";
  const isIframe = mapEmbed.includes("<iframe");

  return (
    <div>
      <SEO title="Motoraduno" description="Un giro in moto sui Colli Bolognesi con partenza e ritorno al Ristorante Masetti di Zola Predosa." />

      {/* HERO */}
      <section className="bg-bordeaux text-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <Reveal direction="up">
            <Bike className="w-10 h-10 mx-auto text-brass mb-4" />
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Un giro in moto sui Colli Bolognesi.</h1>
            <p className="font-accent italic text-2xl sm:text-3xl text-brass mt-2">Poi ci vediamo da Masetti.</p>
            <Button onClick={scrollToForm} className="bg-brass hover:bg-brass-hover text-oak-dark rounded-full px-8 h-12 text-base mt-8 shadow-md" data-testid="moto-cta-top">
              <Bike className="w-4 h-4 mr-2" />Partecipa al giro
            </Button>
          </Reveal>
        </div>
      </section>

      {/* DETTAGLI */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 space-y-6">
          <Reveal direction="up">
            <div className="bg-cream-dark rounded-2xl border border-brass/20 p-6 space-y-4" data-testid="moto-details">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-bordeaux mt-0.5 shrink-0" />
                <p className="text-oak">
                  <span className="font-serif text-lg text-bordeaux block">{settings.moto_date || "Data da definire"}</span>
                  Ritrovo alle <b>{settings.moto_time || "10:30"}</b> al Ristorante Masetti.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-bordeaux mt-0.5 shrink-0" />
                <p className="text-oak">Via Gesso 70, 40069 Zola Predosa (BO)</p>
              </div>
            </div>
          </Reveal>

          <Reveal direction="up" delay={80}>
            <div className="text-oak/85 leading-relaxed">
              <h2 className="font-serif text-2xl text-bordeaux mb-2">Il giro</h2>
              <p>Un giro turistico in moto tra le curve e i panorami dei Colli Bolognesi, in compagnia. Partenza e ritorno al Ristorante Masetti: si parte insieme dal ritrovo e si rientra al ristorante a fine mattinata.</p>
              {settings.moto_route_notes && <p className="mt-3">{settings.moto_route_notes}</p>}
            </div>
          </Reveal>

          {/* MAPPA PERCORSO */}
          <Reveal direction="up" delay={120}>
            <div data-testid="moto-map">
              <h2 className="font-serif text-2xl text-bordeaux mb-2">Il percorso</h2>
              {mapEmbed ? (
                isIframe ? (
                  <div className="rounded-2xl overflow-hidden border border-brass/20 [&_iframe]:w-full [&_iframe]:h-72 [&_iframe]:border-0" dangerouslySetInnerHTML={{ __html: mapEmbed }} />
                ) : (
                  <a href={mapEmbed} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-bordeaux underline underline-offset-4 hover:text-bordeaux-dark" data-testid="moto-map-link">
                    <MapPin className="w-4 h-4" />Apri il percorso sulla mappa
                  </a>
                )
              ) : (
                <div className="rounded-2xl border border-dashed border-brass/40 bg-cream-dark/50 p-8 text-center text-oak/60">
                  <MapPin className="w-6 h-6 mx-auto text-brass/60 mb-2" />
                  Il percorso è in definizione: la mappa sarà pubblicata qui a breve.
                </div>
              )}
            </div>
          </Reveal>

          {/* PRANZO */}
          <Reveal direction="up" delay={160}>
            <div className="bg-white rounded-2xl border border-brass/20 p-6" data-testid="moto-lunch">
              <div className="flex items-center gap-2 mb-2">
                <Utensils className="w-5 h-5 text-bordeaux" />
                <h2 className="font-serif text-2xl text-bordeaux">Al rientro, pranzo facoltativo</h2>
              </div>
              <p className="text-oak/85 leading-relaxed">Chi vuole si ferma a pranzo da Masetti: si ordina <b>alla carta</b>, ognuno sceglie e paga ciò che ordina. Per chi si ferma, <b>acqua, coperto e caffè sono offerti da Masetti</b>.</p>
              <Button onClick={scrollToForm} className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-8 h-12 mt-5 w-full sm:w-auto" data-testid="moto-cta-mid">
                <Bike className="w-4 h-4 mr-2" />Partecipa al giro
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="partecipa" className="bg-cream-dark py-12 sm:py-16 scroll-mt-4">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          {done ? (
            <div className="bg-white rounded-2xl border border-brass/20 p-8 text-center" data-testid="moto-confirm">
              <CheckCircle2 className="w-12 h-12 mx-auto text-brass mb-3" />
              <h2 className="font-serif text-2xl text-bordeaux">Iscrizione ricevuta!</h2>
              <p className="text-oak/80 mt-3 leading-relaxed">Grazie, la tua iscrizione è registrata. Ti aspettiamo al ritrovo da Masetti: per qualsiasi informazione chiamaci al ristorante.</p>
              <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full border-bordeaux/40 text-bordeaux" data-testid="moto-new">Iscrivi un’altra persona</Button>
            </div>
          ) : (
            <>
              <h2 className="font-serif text-2xl sm:text-3xl text-bordeaux text-center mb-1">Partecipa al giro</h2>
              <p className="text-center text-oak/70 text-sm mb-6">Compila il modulo: l'iscrizione è immediata.</p>
              <form onSubmit={submit} noValidate={false} className="space-y-4 bg-white p-6 rounded-2xl border border-brass/20" data-testid="moto-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase tracking-wider text-oak/70">Nome *</Label><Input required value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="moto-nome" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-oak/70">Cognome *</Label><Input required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="moto-cognome" /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-oak/70">Telefono *</Label><Input required type="tel" inputMode="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="es. 333 1234567" data-testid="moto-telefono" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase tracking-wider text-oak/70">Partecipanti totali *</Label><Input required type="number" inputMode="numeric" min="1" max="50" value={form.partecipanti} onChange={(e) => set("partecipanti", e.target.value)} data-testid="moto-partecipanti" /></div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-oak/70">Moto *</Label>
                    <Input required type="number" inputMode="numeric" min="0" max="50" value={form.moto} onChange={(e) => set("moto", e.target.value)} data-testid="moto-moto" />
                    {tried && errors.moto && <p className="text-xs text-bordeaux mt-1" data-testid="moto-error-moto">{errors.moto}</p>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-oak/70">Vi fermate a pranzo? *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[[true, "si", "Sì"], [false, "no", "No"]].map(([v, k, l]) => (
                      <button key={k} type="button" onClick={() => set("pranzo", v)} data-testid={`moto-pranzo-${k}`}
                        className={`px-5 py-2 rounded-full text-sm border transition-colors ${form.pranzo === v ? "bg-bordeaux text-cream border-bordeaux" : "bg-white text-oak border-brass/30 hover:border-bordeaux/50"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {tried && errors.pranzo && <p className="text-xs text-bordeaux mt-1" data-testid="moto-error-pranzo">{errors.pranzo}</p>}
                  {form.pranzo === true && (
                    <div className="mt-3">
                      <Label className="text-xs uppercase tracking-wider text-oak/70">Persone a pranzo *</Label>
                      <Input required type="number" inputMode="numeric" min="1" max={partecipanti || 50} value={form.persone_pranzo} onChange={(e) => set("persone_pranzo", e.target.value)} data-testid="moto-persone-pranzo" />
                      <p className="text-[11px] text-oak/50 mt-1">Massimo {partecipanti || 0} (i partecipanti totali).</p>
                      {(tried || form.persone_pranzo) && errors.persone_pranzo && <p className="text-xs text-bordeaux mt-1" data-testid="moto-error-persone-pranzo">{errors.persone_pranzo}</p>}
                    </div>
                  )}
                  <p className="text-[11px] text-oak/50 mt-2">Si ordina alla carta: acqua, coperto e caffè sono offerti da Masetti.</p>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-oak/70">Note (opzionale)</Label><Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Es. allergie, arrivo in ritardo…" data-testid="moto-note" /></div>

                <div className="flex items-start gap-2 rounded-xl bg-cream-dark/60 border border-brass/15 p-3">
                  <ShieldCheck className="w-4 h-4 text-brass mt-0.5 shrink-0" />
                  <p className="text-[12px] text-oak/70 leading-relaxed">
                    I dati inseriti sono usati esclusivamente per organizzare il motoraduno e il pranzo e non vengono condivisi con terzi.{" "}
                    <Link to="/privacy" className="text-bordeaux underline underline-offset-2" data-testid="moto-privacy-link">Informativa privacy</Link>.
                  </p>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-brass hover:bg-brass-hover text-oak-dark h-12 rounded-full text-base" data-testid="moto-submit">
                  {loading ? "Invio…" : "Partecipa al giro"}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
